import type { Faq } from '../components/FaqList'
import type { ServiceCategory } from '../data/serviceCategories'
import { categoryContent } from '../data/categoryContent'
import { serviceOverrides } from '../data/serviceOverrides'

type ServiceKind =
  | 'diagnostics'
  | 'testing'
  | 'inspection'
  | 'replacement'
  | 'repair'
  | 'maintenance'
  | 'consultation'
  | 'fleet'

export type ServiceContent = {
  metaTitle: string
  metaDescription: string
  intro: string
  whenNeeded: string[]
  situationNote: string
  whatWeCheck: string[]
  steps: { label: string; copy: string }[]
  faqs: Faq[]
}

const situationNotes: Record<ServiceKind, string> = {
  diagnostics:
    'Symptoms like these can come from more than one component, which is why testing comes before any recommendation.',
  repair:
    'Symptoms like these can come from more than one component, which is why testing comes before any recommendation.',
  replacement:
    'Symptoms like these can come from more than one component, which is why testing comes before any recommendation.',
  testing:
    'Testing provides measurements that help separate the reported symptom from the actual cause.',
  inspection:
    'These are practical reasons to have the vehicle reviewed before making travel, purchase, or repair decisions.',
  maintenance:
    'Maintenance needs vary by vehicle, mileage, use, and service history, so we confirm what applies before recommending work.',
  consultation:
    'Bring the vehicle’s mileage, service history, and the questions you want help prioritizing.',
  fleet:
    'Call to discuss the vehicles you operate, how they are used, and the maintenance or repair support you need.',
}

const diagnosticSteps = [
  {
    label: 'Condition',
    copy: 'We start with what you are experiencing and verify it on the vehicle.',
  },
  {
    label: 'Cause',
    copy: 'We test until the cause is confirmed instead of replacing parts on a guess.',
  },
  {
    label: 'Correction',
    copy: 'We explain the findings and the options in plain language.',
  },
  {
    label: 'Confirm',
    copy: 'We verify the concern is resolved before the vehicle goes back to you.',
  },
]

const stepsByKind: Record<ServiceKind, { label: string; copy: string }[]> = {
  diagnostics: diagnosticSteps,
  testing: diagnosticSteps,
  repair: diagnosticSteps,
  replacement: diagnosticSteps,
  inspection: [
    {
      label: 'Review',
      copy: 'We go over the vehicle and the concerns you want covered.',
    },
    {
      label: 'Check',
      copy: 'We inspect the items included in this inspection.',
    },
    {
      label: 'Explain',
      copy: 'We tell you what we found in plain language.',
    },
    {
      label: 'Document',
      copy: 'You leave knowing what should be addressed now and what can be monitored.',
    },
  ],
  maintenance: [
    {
      label: 'Confirm',
      copy: 'We confirm what your specific vehicle and mileage call for.',
    },
    {
      label: 'Inspect',
      copy: 'We check condition and levels for the related items.',
    },
    {
      label: 'Service',
      copy: 'We perform the maintenance the vehicle is due for.',
    },
    {
      label: 'Plan',
      copy: 'We go over what is coming due so you can plan ahead.',
    },
  ],
  consultation: [
    {
      label: 'History',
      copy: 'We start with the vehicle’s mileage, use and service history.',
    },
    {
      label: 'Priorities',
      copy: 'We talk through the concerns and questions you want answered.',
    },
    {
      label: 'Plan',
      copy: 'We lay out what to address and in what order.',
    },
    {
      label: 'Next steps',
      copy: 'You decide what to schedule and when.',
    },
  ],
  fleet: [
    {
      label: 'Vehicles',
      copy: 'We go over the vehicles you operate and how they are used.',
    },
    {
      label: 'Schedule',
      copy: 'We work out what to bring in and when.',
    },
    {
      label: 'Communicate',
      copy: 'We explain findings before any work is approved.',
    },
    {
      label: 'Follow through',
      copy: 'We complete the approved work and tell you what is coming next.',
    },
  ],
}

/** Plain-language name of the system each category covers. */
const systemName: Record<string, string> = {
  'diagnostics-and-inspections': 'system being tested',
  'preventive-maintenance': 'maintenance item',
  'brake-services': 'braking system',
  'steering-and-suspension': 'steering and suspension system',
  'tires-and-wheels': 'tire and wheel assembly',
  'heating-and-air-conditioning': 'A/C and heating system',
  'engine-repair': 'engine',
  'cooling-system': 'cooling system',
  'electrical-system': 'electrical system',
  'fuel-and-ignition-systems': 'fuel and ignition system',
  'transmission-and-drivetrain': 'transmission and drivetrain',
  'exhaust-and-emissions': 'exhaust and emissions system',
  'additional-services': 'component being serviced',
}

const TRAILING_WORDS =
  /(diagnostics|diagnosis|testing|test|inspections|inspection|replacement|replace|repairs|repair|services|service|maintenance|changes|change|flush|rotation|balancing|recovery|evacuation|recharge|consultations|consultation|sensor|tune-ups|and|&|,)/i

function kindOf(name: string): ServiceKind {
  const n = name.toLowerCase()
  if (n.includes('consultation')) return 'consultation'
  if (n.includes('fleet')) return 'fleet'
  if (n.includes('inspection')) return 'inspection'
  if (n.includes('diagnostic') || n.includes('diagnosis')) return 'diagnostics'
  if (n.includes('testing') || n.endsWith('test')) return 'testing'
  if (n.includes('replacement')) return 'replacement'
  if (n.includes('repair')) return 'repair'
  if (
    n.includes('service') ||
    n.includes('maintenance') ||
    n.includes('flush') ||
    n.includes('changes') ||
    n.includes('rotation') ||
    n.includes('balancing')
  )
    return 'maintenance'
  return 'maintenance'
}

/** A safe, grammatical noun phrase for the component a service acts on. */
function componentPhrase(name: string, categorySlug: string): string {
  const fallback = systemName[categorySlug] ?? 'component being serviced'

  let words = name.split(/\s+/)
  while (words.length > 0 && TRAILING_WORDS.test(words[words.length - 1])) {
    words = words.slice(0, -1)
  }
  const stripped = words.join(' ').trim()

  if (
    stripped.length === 0 ||
    stripped.includes('/') ||
    /^(vehicle|general|factory scheduled|used vehicle|pre-purchase vehicle|road-trip)$/i.test(
      stripped,
    )
  ) {
    return fallback
  }

  // Keep acronyms and model designations as written; lowercase normal words.
  return stripped
    .split(' ')
    .map((word) =>
      /^[A-Z0-9\-]+$/.test(word) || /\d/.test(word) ? word : word.toLowerCase(),
    )
    .join(' ')
}

function buildIntro(
  kind: ServiceKind,
  name: string,
  phrase: string,
  categoryName: string,
): string {
  switch (kind) {
    case 'diagnostics':
      return `${name} at East End Automotive Repair starts with testing rather than assumptions. We verify the concern on the vehicle, test the ${phrase}, and explain the confirmed cause before any repair is recommended.`
    case 'testing':
      return `${name} produces measured results instead of a guess. We test the ${phrase} and go over what those readings mean for your vehicle before anything is recommended.`
    case 'inspection':
      return `${name} is a review of your vehicle’s current condition in this area. We inspect, tell you what we found, and separate what should be addressed now from what can reasonably wait.`
    case 'replacement':
      return `${name} at East End Automotive Repair follows inspection. We confirm the ${phrase} is the source of your concern first, then explain the correction and the options before any work is approved.`
    case 'repair':
      return `${name} at East End Automotive Repair begins with confirming what is actually wrong with the ${phrase}, so the repair addresses the cause rather than the symptom.`
    case 'consultation':
      return `${name} is a conversation about your vehicle rather than a repair. We review where it stands and help you plan what to address and in what order.`
    case 'fleet':
      return `${name} is about keeping your vehicles available. We handle the work and communicate findings clearly so you can decide what to schedule.`
    case 'maintenance':
    default:
      return `${name} is scheduled service rather than a repair for a specific fault. Requirements vary by vehicle, so we confirm what yours calls for and go over anything else it is due for. This is part of our ${categoryName.toLowerCase()} work.`
  }
}

function buildWhenNeeded(
  kind: ServiceKind,
  categorySymptoms: string[],
  phrase: string,
): string[] {
  const base = categorySymptoms.slice(0, 4)
  switch (kind) {
    case 'inspection':
      return [
        ...base,
        'You want the vehicle reviewed before deciding what to repair next',
      ]
    case 'maintenance':
      return [
        ...base,
        'You are approaching a service interval, or the last service is unknown',
      ]
    case 'replacement':
    case 'repair':
      return [
        ...base,
        `Signs that may be related to the ${phrase}, which we confirm by inspection before replacing anything`,
      ]
    case 'consultation':
    case 'fleet':
      return base
    default:
      return [
        ...base,
        'You want the cause confirmed by testing before parts are replaced',
      ]
  }
}

function buildWhatWeCheck(
  kind: ServiceKind,
  phrase: string,
  categoryName: string,
): string[] {
  switch (kind) {
    case 'diagnostics':
      return [
        'The reported concern, verified on the vehicle before testing begins',
        `Testing of the ${phrase} with modern diagnostic equipment`,
        'Related components that can produce the same symptom',
        'A plain-language explanation of the confirmed cause and the recommended correction',
      ]
    case 'testing':
      return [
        `Measured results from testing the ${phrase}`,
        'How those results compare to how the system should be performing',
        'Related components that could affect the readings',
        'What the results mean, explained before anything is recommended',
      ]
    case 'inspection':
      return [
        'The condition of the items covered by this inspection',
        'Signs of wear, damage or leakage in the surrounding area',
        'Any warning lights or concerns you reported',
        'What should be addressed now versus what can be monitored',
      ]
    case 'replacement':
      return [
        `Whether the ${phrase} is the source of your concern, confirmed by inspection`,
        'Condition of the related components in the same system',
        'The correction we recommend and the options available',
        'Verification that the concern is resolved after the work is complete',
      ]
    case 'repair':
      return [
        'The concern as you described it, verified on the vehicle',
        `Testing to isolate the fault in the ${phrase}`,
        'Whether repair or replacement is the appropriate correction',
        'A confirmation check once the work is finished',
      ]
    case 'consultation':
      return [
        'Your vehicle’s mileage, history and how it is driven',
        'The maintenance its factory schedule calls for',
        'Concerns you have noticed and want reviewed',
        'A practical order of priority for the work',
      ]
    case 'fleet':
      return [
        'Maintenance due across the vehicles you bring in',
        'Repair concerns reported by your drivers',
        'What needs attention now versus what can be scheduled',
        'Clear communication of findings before work is approved',
      ]
    case 'maintenance':
    default:
      return [
        `What your specific vehicle’s schedule calls for in ${categoryName.toLowerCase()}`,
        'Condition of the related components and fluid levels where applicable',
        'Anything found during service that you should know about',
        'What can safely wait until the next visit',
      ]
  }
}

function buildFaqs(kind: ServiceKind, name: string, phrase: string): Faq[] {
  const callFaq: Faq = {
    question: 'What is the fastest way to get an answer?',
    answer:
      'Call the shop during business hours and describe what the vehicle is doing. We will tell you what testing or service it needs and when we can get it in.',
  }

  switch (kind) {
    case 'diagnostics':
      return [
        {
          question: `How is ${name} handled at East End?`,
          answer: `We verify the concern, test the ${phrase}, confirm the cause, and then explain the correction. Nothing is replaced on a guess.`,
        },
        {
          question: 'Will you tell me what you found before any repair?',
          answer:
            'Yes. We explain the confirmed cause and the recommended correction so you can decide what happens next.',
        },
        callFaq,
      ]
    case 'testing':
      return [
        {
          question: `What do I get from ${name}?`,
          answer: `Measured results for the ${phrase} and an explanation of what those results mean, so any recommendation that follows is based on data.`,
        },
        {
          question: 'Does testing include the repair?',
          answer:
            'No. Testing establishes what is happening. Any repair work is explained and approved by you separately.',
        },
        callFaq,
      ]
    case 'inspection':
      return [
        {
          question: 'What do I receive from this inspection?',
          answer:
            'An explanation of what we found, separated into what should be addressed now and what can be monitored.',
        },
        {
          question: 'Does an inspection include repairs?',
          answer:
            'No. An inspection identifies conditions. Any repair work is quoted and approved by you separately.',
        },
        callFaq,
      ]
    case 'replacement':
      return [
        {
          question: 'Will you confirm the part is the cause before replacing it?',
          answer: `Yes. We confirm the ${phrase} is the source of the concern before recommending replacement, and we explain the options first.`,
        },
        {
          question: 'Is the replacement work covered by a warranty?',
          answer:
            'Repairs we perform carry a 1 year / 12,000 mile parts and labor warranty. Inspection and diagnostic time is separate from that coverage.',
        },
        callFaq,
      ]
    case 'repair':
      return [
        {
          question: 'Do you repair or replace?',
          answer: `That depends on what inspection and testing show for the ${phrase}. We explain the finding and the recommended correction before work begins.`,
        },
        {
          question: 'Is the repair covered by a warranty?',
          answer:
            'Repairs we perform carry a 1 year / 12,000 mile parts and labor warranty. Inspection and diagnostic time is separate from that coverage.',
        },
        callFaq,
      ]
    case 'consultation':
    case 'fleet':
      return [
        {
          question: 'What should I have ready when I call?',
          answer:
            'Year, make, model, current mileage, and any concerns or service records you want covered.',
        },
        {
          question: 'Will you explain everything before any work is approved?',
          answer:
            'Yes. Findings and recommendations are explained first so you can decide what to schedule.',
        },
        callFaq,
      ]
    case 'maintenance':
    default:
      return [
        {
          question: 'How do I know if my vehicle is due for this service?',
          answer:
            'Intervals vary by vehicle and how it is driven. Call with your year, make, model and mileage and we can review what applies to yours.',
        },
        {
          question: 'Will you tell me what else is due?',
          answer:
            'Yes. We separate what is due now from what can reasonably wait so maintenance can be planned out.',
        },
        callFaq,
      ]
  }
}

export function relatedHeading(categoryName: string): string {
  return /services$/i.test(categoryName)
    ? `Related ${categoryName}`
    : `Related ${categoryName} services`
}

export function buildServiceContent(
  category: ServiceCategory,
  service: { name: string; slug: string },
): ServiceContent {
  const content = categoryContent[category.slug]
  const kind = kindOf(service.name)
  const phrase = componentPhrase(service.name, category.slug)
  const override = serviceOverrides[`${category.slug}/${service.slug}`] ?? {}

  return {
    metaTitle: `${service.name} in Oak Ridge, TN | East End Automotive Repair`,
    metaDescription: `${service.name} in Oak Ridge, TN at East End Automotive Repair. Diagnosis-first ${category.name.toLowerCase()} with clear explanations. Call (865) 206-2279.`,
    intro:
      override.intro ??
      buildIntro(kind, service.name, phrase, category.name),
    whenNeeded:
      override.whenNeeded ??
      buildWhenNeeded(kind, content?.symptoms ?? [], phrase),
    situationNote: situationNotes[kind],
    steps: stepsByKind[kind],
    whatWeCheck:
      override.whatWeCheck ??
      buildWhatWeCheck(kind, phrase, category.name),
    faqs: override.faqs ?? buildFaqs(kind, service.name, phrase),
  }
}
